/**
 * PRTL-1519: the web app's fetch for tRPC, safe against its own token refresh.
 *
 * `auth.refreshToken` revokes the token it replaces. A request already sent with the old
 * token (page load fires a dozen queries while the daily refresh runs) is then rejected,
 * and the web app treated any rejection as a dead session and logged the user out.
 *
 * This wrapper never refreshes and never logs out. When the server rejects a request's
 * token, it waits for any refresh in flight (every concurrent rejection awaits the same
 * one), and if the token has changed since the request was sent, re-sends it ONCE with the
 * new token. Otherwise it hands back the rejection, so a genuinely revoked or expired
 * session still signs out exactly as before.
 *
 * What counts as a rejection: a 401 (every operation in the batch failed authentication,
 * which the server checks in middleware before any procedure runs, so nothing ran and a
 * mutation is safe to re-send); or a 207 on a query batch (GET) whose every error is
 * UNAUTHORIZED. A 207 on a mutation batch is never re-sent: part of it may have run.
 *
 * The navigator has its own, fuller version (lib/auth/sessionCoordinator.ts, PRTL-1461).
 */

export interface AuthRetryDeps {
    fetch(url: string, init: RequestInit): Promise<Response>;
    /** The current bearer token. */
    getToken(): string | null;
    /** Resolves once no refresh is in flight. */
    waitForRefresh(): Promise<void>;
}

export function bearerOf(headers: HeadersInit | undefined): string | null {
    const value = new Headers(headers).get("Authorization");
    return value?.startsWith("Bearer ") ? value.slice("Bearer ".length) : null;
}

/** Whether the server refused this response's request for its token (see above). */
export async function isAuthRejection(
    response: Response,
    method: string | undefined,
): Promise<boolean> {
    if (response.status === 401) return true;
    if (response.status !== 207 || (method ?? "GET").toUpperCase() !== "GET") return false;
    try {
        const body: unknown = await response.clone().json();
        if (!Array.isArray(body)) return false;
        const codes = body
            .map((item: unknown) =>
                item && typeof item === "object" && "error" in item
                    ? (item as { error?: { data?: { code?: unknown } } }).error?.data?.code
                    : undefined,
            )
            .filter((code) => code !== undefined);
        return codes.length > 0 && codes.every((code) => code === "UNAUTHORIZED");
    } catch {
        return false;
    }
}

export function createAuthRetryFetch(deps: AuthRetryDeps) {
    return async (url: string, init: RequestInit): Promise<Response> => {
        const response = await deps.fetch(url, init);
        if (!(await isAuthRejection(response, init.method))) return response;
        await deps.waitForRefresh();
        const current = deps.getToken();
        if (!current || current === bearerOf(init.headers)) return response;
        const headers = new Headers(init.headers);
        headers.set("Authorization", `Bearer ${current}`);
        return deps.fetch(url, { ...init, headers });
    };
}
